import React, { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import styles from "./chatwindow.module.css";
import ThoughtsDrawer from "../ThoughtsDrawer";
import AgentTimeline from "../AgentTimeline";
import { timelineData } from "../../data/mock";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import { ChatMessage, sendUserPreferenceToAgent } from "../../store/userSlice";
import { defaultSteps, getStepsByAgentId } from "../../utils/constants";
import StaticProgressTracker from "../StaticProgressTracker";
import StreamProgressTracker from "../StreamProgressTracker";
import AgentResponse from "../AgentResponse";
import { copyAgentContent } from "../../utils/copyUtils";
import { safeJSONParse, extractFileFromContent } from "../../utils/helper";

import {
  Chip,
  Button,
  Typography,
  Box,
  Paper,
  Popover,
  IconButton,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";

import jsPDF from "jspdf";
import {
  formatContentForPDF,
  addPDFHeader,
  generateTextPDF,
  hasArabicCharacters,
  generateMixedContentPDF,
  generatePDFFileName,
  getCustomPDFHeader,
} from "../../utils/pdfUtils";
import { Cursor } from "recharts/types/component/Cursor";

export interface ProgressStep {
  id: string;
  title: string;
}

type IconData = {
  id: number;
  paths: string[];
  rectRadius?: number;
};

const progressExcludedAgents = [
  "utility_agent",
  "workflow_end",
  "hr_agent_v2_hitl",
  "validation_check",
  "no_candidates_found",
  "answer_composer_agent",
];

const feedbackExcludedAgents = [
  "utility_agent",
  "resume_fetcher_agent",
  "validation_check",
  "no_candidates_found",
  // "template_reviewer_agent", // Removed - now accepts feedback
  "socialmedia_intake_agent",
  "press_release_qa_agent",
  "PressReleaseQAAgent",
  // "contract_reviewer_orchestrator", // Removed - now accepts feedback
  // "ConsolidatorAgent",
  // "consolidator_agent",
  // "template_reviewer_intake_agent",
  // "template_reviewer_checklist_agent",
  // "template_guided_intake_agent",
  // "template_document_selector_agent",
  // "template_drafter_agent",
  // "template_reviewer_qa_agent",
  // "template_request_orchestrator",
  "answer_composer_agent",
];

const downloadExcludedAgents = [
  "email_drafting",
  "translation_agent",
  "contract_reviewer_orchestrator",
  "ConsolidatorAgent",
  "consolidator_agent",
  "template_reviewer_intake_agent",
  "template_reviewer_checklist_agent",
  "template_guided_intake_agent",
  "template_document_selector_agent",
  "template_drafter_agent",
  "template_reviewer_qa_agent",
  "template_request_orchestrator",
  "template_reviewer_agent",
  "sow_auto_filler",
];

const iconsData: IconData[] = [
  {
    id: 0,
    paths: [
      "M9.84862 17.2328H14.9291C15.3928 17.2328 15.7739 16.9493 15.9393 16.5417L17.6259 12.5423C17.6761 12.4123 17.7027 12.2764 17.7027 12.1287V10.9886C17.7027 10.3653 17.2005 9.6712 16.5862 9.6712H13.0653L13.5969 7.26388L13.6147 7.08666C13.6147 6.85331 13.5202 6.64064 13.3695 6.48704L12.7728 5.89038L9.0511 9.65053C8.85025 9.85434 8.72619 10.1379 8.72619 10.451V16.1222C8.72619 16.7455 9.23424 17.2328 9.84862 17.2328ZM5.41797 10.6164H7.30838V17.2328H5.41797V10.6164Z",
    ],
  },
  {
    id: 1,
    paths: [
      "M13.275 5.89038H8.19451C7.73077 5.89038 7.34973 6.17394 7.18432 6.58156L5.49477 10.581C5.44455 10.7109 5.41797 10.8468 5.41797 10.9945V12.1346C5.41797 12.7579 5.92011 13.452 6.53449 13.452H10.0583L9.52666 15.8593L9.50894 16.0366C9.50894 16.2699 9.60346 16.4826 9.7541 16.6362L10.3449 17.2299L14.0725 13.4727C14.2734 13.2689 14.3974 12.9853 14.3974 12.6722V7.001C14.3974 6.37775 13.8894 5.89038 13.275 5.89038ZM15.8152 5.89038H17.7056V12.5068H15.8152V5.89038Z",
    ],
  },
  {
    id: 2,
    paths: [
      "M12 6.75C11.5858 6.75 11.25 7.08579 11.25 7.5V12.1893L9.53033 10.4697C9.23744 10.1768 8.76256 10.1768 8.46967 10.4697C8.17678 10.7626 8.17678 11.2374 8.46967 11.5303L11.4697 14.5303C11.7626 14.8232 12.2374 14.8232 12.5303 14.5303L15.5303 11.5303C15.8232 11.2374 15.8232 10.7626 15.5303 10.4697C15.2374 10.1768 14.7626 10.1768 14.4697 10.4697L12.75 12.1893V7.5C12.75 7.08579 12.4142 6.75 12 6.75Z",
      // Bottom tray / PDF bar
      "M7 15.25C6.58579 15.25 6.25 15.5858 6.25 16C6.25 16.4142 6.58579 16.75 7 16.75H17C17.4142 16.75 17.75 16.4142 17.75 16C17.75 15.5858 17.4142 15.25 17 15.25H7Z",
    ],
  },
  {
    id: 3,
    paths: [
      "M14.3962 7.39686H17.0546C17.1521 7.39686 17.2318 7.31711 17.2318 7.21964C17.2318 6.97743 17.1225 6.74703 16.9364 6.59048L14.6591 4.69416C14.5144 4.57306 14.2397 4.47559 14.0506 4.47559C13.9295 4.47559 13.832 4.57306 13.832 4.69416V6.83565C13.835 7.14579 14.0861 7.39686 14.3962 7.39686Z",
      "M13.0685 6.83567V4.47266H9.19911C8.67925 4.47266 8.25391 4.898 8.25391 5.41786V15.3425C8.25391 15.8624 8.67925 16.2877 9.19911 16.2877H16.2882C16.808 16.2877 17.2334 15.8624 17.2334 15.3425V8.16487H14.3977C13.6652 8.16487 13.0685 7.5682 13.0685 6.83567Z",
      "M7.42658 16.1695V6.36304H6.83583C6.31597 6.36304 5.89062 6.78838 5.89062 7.30824V17.7055C5.89062 18.2254 6.31597 18.6507 6.83583 18.6507H14.3975C14.9173 18.6507 15.3427 18.2254 15.3427 17.7055V17.1147H8.37179C7.85193 17.1147 7.42658 16.6894 7.42658 16.1695Z",
    ],
  },
];

export type Message = {
  id: string;
  sender: "user" | "agent";
  text: JSX.Element | string;
  timestamp?: number;
  backendMessageId?: string; // Backend message_id for feedback
  isPersisted?: boolean; // Whether create API has completed
};

type MessageBubbleProps = {
  // messages: Message[];
  currentChat: ChatMessage[];
  userAvatar?: string;
  agentAvatar?: string;
  sessionId?: string; // Backend session_id for feedback
  showThinkingBubble?: boolean;
  inputRef?: React.RefObject<any>;
};

// Helper function to infer executor from SOW message content
function inferSowExecutor(messageContent: string): string | null {
  const parsedContent = safeJSONParse(messageContent);
  if (!parsedContent) return null;

  const finalAnswer = parsedContent?.finalAnswer || parsedContent;
  const status = finalAnswer?.status || finalAnswer?.state;
  const message = finalAnswer?.message || "";

  // Infer executor based on status and message patterns
  if (status === "awaiting_bu_category_confirmation") {
    return "sow_pre_analyzer";
  }

  if (status === "awaiting_contract_name") {
    return "sow_auto_filler";
  }

  if (status === "awaiting_confirmation") {
    // Check if it's a question answer or document analysis
    if (
      message.includes("Answer to your question:") ||
      message.includes("**Answer to your question:**")
    ) {
      return "sow_question_answerer";
    }
    // Otherwise it's the document analyzer showing SOW Auto-Fill Summary
    return "sow_document_analyzer";
  }

  if (status === "completed") {
    return "sow_final_generator";
  }

  // Default to sow_auto_filler for other statuses
  return "sow_auto_filler";
}

export function getAgentSteps(
  agentIdName?: string | null,
  isImageGeneration?: boolean,
  messageContent?: string,
): ProgressStep[] {
  let idToUse = agentIdName || undefined;

  // For SOW agents, infer the specific executor from message content
  if (agentIdName === "sow_auto_filler" && messageContent) {
    const inferredExecutor = inferSowExecutor(messageContent);
    if (inferredExecutor) {
      idToUse = inferredExecutor;
    }
  }

  // For contract reviewer, check if it's a QA agent response
  if (agentIdName === "contract_reviewer_orchestrator" && messageContent) {
    try {
      const parsedContent = safeJSONParse(messageContent);
      // Check if response array contains contract_reviewer_qa_agent
      if (
        parsedContent?.response &&
        Array.isArray(parsedContent.response) &&
        parsedContent.response.some(
          (item: any) => item.agent_id === "contract_reviewer_qa_agent",
        )
      ) {
        console.log("Detected QA agent in message - switching to QA steps");
        idToUse = "contract_reviewer_qa_agent";
      }
    } catch (e) {
      console.warn("Error checking for QA agent in message:", e);
    }
  }

  const steps = getStepsByAgentId(idToUse, isImageGeneration);
  return steps.length > 0 ? steps : defaultSteps;
}

// Helper function to check if message contains a question WITHOUT substantial response
const messageHasQuestionOnly = (messageContent: string): boolean => {
  const parsedContent = safeJSONParse(messageContent);
  if (!parsedContent) return false;

  // Check for question at top level or in finalAnswer
  const question =
    parsedContent.question ||
    parsedContent?.finalAnswer?.question ||
    parsedContent?.result?.question;

  // Only return true if there's a question AND no substantial result/response data
  if (!question || question.trim().length === 0) {
    return false;
  }

  // Check if there's substantial response data alongside the question
  const hasSubstantialData =
    parsedContent.result ||
    parsedContent.response ||
    (parsedContent.finalAnswer &&
      Object.keys(parsedContent.finalAnswer).length > 1);

  // Hide progress only when there's a question but NO substantial data
  return !hasSubstantialData;
};

const MessageBubble: React.FC<MessageBubbleProps> = ({
  // messages,
  currentChat,
  userAvatar,
  agentAvatar,
  sessionId,
  showThinkingBubble = false,
  inputRef,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [feedbackPopupState, setFeedbackPopupState] = useState<{
    messageId: string | null;
    anchorEl: HTMLElement | null;
  }>({ messageId: null, anchorEl: null });
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const actionRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const dispatch = useAppDispatch();

  const { loading, isStreaming, streamProgress } = useSelector(
    (state: RootState) => state.user,
  );

  console.log("CURRENT CHAT", currentChat);

  // Smooth scroll function with custom easing
  const smoothScrollToBottom = (duration = 400) => {
    if (!chatWindowRef.current) return;

    const start = chatWindowRef.current.scrollTop;
    const end = chatWindowRef.current.scrollHeight;
    const change = end - start;
    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic for smooth deceleration
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      if (chatWindowRef.current) {
        chatWindowRef.current.scrollTop = start + change * easeProgress;
      }

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  };

  // Auto-scroll immediately when user sends a message
  useEffect(() => {
    smoothScrollToBottom(600); // 600ms duration - slower, more elegant
  }, [currentChat]);

  // Auto-scroll to bottom when agent response completes
  useEffect(() => {
    if (!loading && !isStreaming) {
      smoothScrollToBottom(600); // 600ms duration - slower, more elegant
    }
  }, [loading, isStreaming]);

  // Auto-scroll when stream progress updates (progress tracker changes)
  useEffect(() => {
    if (isStreaming && streamProgress.length > 0) {
      smoothScrollToBottom(400); // Shorter duration for smoother feel during streaming
    }
  }, [streamProgress, isStreaming]);

  // Helper function to get isImageGeneration flag from the most recent user message
  const getIsImageGenerationFlag = (
    currentIndex: number,
  ): boolean | undefined => {
    // Look backwards from current message to find the most recent user message
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (currentChat[i]?.sender === "user") {
        return currentChat[i]?.isImageGeneration;
      }
    }
    return undefined;
  };

  const handleIconClick = async (
    iconId: number,
    messageId: string,
    content: string,
    agentId?: string,
    chatId?: number,
    currentUserPreference?: 0 | 1 | null,
    anchorElement?: HTMLElement,
  ) => {
    console.log("handleIconClick called with:", {
      iconId,
      messageId,
      chatId,
      currentUserPreference,
      anchorElement,
    });

    try {
      if (iconId === 0) {
        // Thumbs up
        const isCurrentlyLiked = currentUserPreference === 1;

        // Call API to save preference if chatId exists and user is liking (not unliking)
        if (chatId && !isCurrentlyLiked) {
          console.log("Sending like preference for chatId:", chatId);

          try {
            await dispatch(
              sendUserPreferenceToAgent({
                chatId,
                userPreference: 1,
                messageId,
              }),
            ).unwrap();
          } catch (error) {
            console.error("API error:", error);
          }

          // Show feedback popup after like attempt
          if (anchorElement) {
            console.log("Setting popup state with anchor:", anchorElement);
            setFeedbackPopupState({
              messageId,
              anchorEl: anchorElement,
            });
          } else {
            console.log("No anchor element found");
          }
        }
      } else if (iconId === 1) {
        // Thumbs down
        const isCurrentlyDisliked = currentUserPreference === 0;

        // Call API to save preference if chatId exists and user is disliking (not undisliking)
        if (chatId && !isCurrentlyDisliked) {
          console.log("Sending dislike preference for chatId:", chatId);

          try {
            await dispatch(
              sendUserPreferenceToAgent({
                chatId,
                userPreference: 0,
                messageId,
              }),
            ).unwrap();
          } catch (error) {
            console.error("API error:", error);
          }

          // Show feedback popup after dislike attempt
          if (anchorElement) {
            console.log("Setting popup state with anchor:", anchorElement);
            setFeedbackPopupState({
              messageId,
              anchorEl: anchorElement,
            });
          } else {
            console.log("No anchor element found");
          }
        }
      } else if (iconId === 3) {
        // Copy functionality
        const success = await copyAgentContent(content, agentId);
        if (success) {
          setCopiedMessageId(messageId);
        }
      } else if (iconId === 2) {
        // Download as PDF functionality
        const pdf = new jsPDF();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 20;
        const maxWidth = pageWidth - 2 * margin;
        const lineHeight = 7;

        // Check if agent has custom header, otherwise format agent name
        const customHeader = getCustomPDFHeader(content, agentId);

        // Only add header if not redrafter_orchestrator (content-only PDF)
        if (agentId !== "redrafter_orchestrator") {
          const headerText =
            customHeader ||
            (agentId
              ? agentId
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase())
              : "Agent");
          addPDFHeader(pdf, headerText, margin, pageWidth);
        }

        // Format content based on agent type
        const formattedText = formatContentForPDF(content, agentId);

        // Check if content contains Arabic characters
        const containsArabic = hasArabicCharacters(formattedText);

        // Agents that should use canvas-based rendering for proper text direction
        const isLocalizerAgent =
          agentId === "LocalizerAgent" || agentId === "localizer_agent";
        const isSocialMediaAgent = agentId === "social_media_insights_agent";
        const isTranslationAgent = agentId === "translation_agent";

        // Generate custom file name based on agent type
        const fileName = generatePDFFileName(content, agentId);

        // Use canvas-based rendering for Arabic content or mixed content agents
        if (
          isLocalizerAgent ||
          (isSocialMediaAgent && containsArabic) ||
          (isTranslationAgent && containsArabic)
        ) {
          generateMixedContentPDF(
            pdf,
            formattedText,
            margin,
            pageWidth,
            pageHeight,
            agentId || "agent",
          )
            .then(() => {
              pdf.save(fileName);
            })
            .catch((error) => {
              console.error("Error rendering mixed content:", error);
              // Fallback to text-based PDF
              generateTextPDF(
                pdf,
                formattedText,
                margin,
                maxWidth,
                pageHeight,
                lineHeight,
                agentId,
              );
              pdf.save(fileName);
            });
        } else {
          // For all other agents, use text-based PDF
          generateTextPDF(
            pdf,
            formattedText,
            margin,
            maxWidth,
            pageHeight,
            lineHeight,
            agentId,
          );
          pdf.save(fileName);
        }
      }
      // Add other icon functionalities here as needed
    } catch (error) {
      console.error("Error in handleIconClick:", error);
      // Don't break UI on error
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <div className={styles.chatWindow} ref={chatWindowRef}>
      <div className={styles.messageContainer} ref={messageContainerRef}>
        {/* Chat messages will be rendered here */}
        {currentChat?.filter((msg) => {
          // Filter out RFQ action messages
          if (msg.sender === "user") {
            try {
              const parsed = JSON.parse(msg.message);
              if (parsed.action === "submit_answers" || parsed.action === "confirm_data") {
                return false; // Hide this message
              }
            } catch (e) {
              // Not JSON, keep it
            }
          }
          return true;
        }).map((msg, index) => {
          // Filter icons based on agent type
          const visibleIcons =
            msg.agentIdName && downloadExcludedAgents.includes(msg.agentIdName)
              ? iconsData.filter((icon) => icon.id !== 2) // Exclude download icon (id: 2)
              : iconsData; // Show all icons

          return (
            <div
              key={msg.id}
              className={clsx(
                styles.messageBubble,
                msg.sender === "user" && styles.userBubble,
              )}
            >
              {msg.sender === "agent" && agentAvatar && (
                <div className={styles.avatar}>
                  <img
                    src={agentAvatar}
                    alt="Agent avatar"
                    height={24}
                    aria-label="Agent avatar"
                    role="img"
                  />
                </div>
              )}
              <div style={{ width: "100%" }}>
                <div
                  className={clsx(
                    styles.message,
                    msg.sender === "user" && styles.userMessage,
                  )}
                >
                  {msg.sender !== "agent" ? (
                    <div>
                      {(() => {
                        // Extract file information from content for historical messages
                        const { hasFile, fileName, cleanedContent } =
                          extractFileFromContent(msg.message);

                        return (
                          <>
                            {/* Show cleaned content (without full file text) */}
                            {cleanedContent && !hasFile && <div>{cleanedContent}</div>}

                            {/* Show file attachment if detected from content */}
                            {hasFile && fileName && (
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "8px",
                                  marginTop: cleanedContent && cleanedContent !== `Uploaded file: ${fileName}` ? "8px" : "0",
                                }}
                              >
                                <Chip
                                  icon={<AttachFileIcon />}
                                  label={fileName}
                                  size="small"
                                  variant="outlined"
                                />
                              </div>
                            )}

                            {/* Show files array (for live chat mode) */}
                            {msg.files && msg.files.length > 0 && (
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "8px",
                                  marginTop: "8px",
                                }}
                              >
                                {msg.files.map((file, index) => (
                                  <Chip
                                    key={index}
                                    icon={<AttachFileIcon />}
                                    label={`${file.name} (${formatFileSize(file.size)})`}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <div>
                      {/* Call helper function directly - Hide progress tracker if message contains a question without substantial response (only for contract reviewer) */}
                      {msg.agentIdName &&
                        !progressExcludedAgents.includes(msg.agentIdName) &&
                        !(
                          msg.agentIdName ===
                            "contract_reviewer_orchestrator" &&
                          messageHasQuestionOnly(msg.message)
                        ) && (
                          <StaticProgressTracker
                            steps={getAgentSteps(
                              msg.agentIdName,
                              getIsImageGenerationFlag(index),
                              msg.message,
                            )}
                          />
                        )}
                      <div
                        className={clsx(
                          "message-text-wrapper",
                          (msg.agentIdName === "LocalizerAgent" ||
                            msg.agentIdName === "localizer_agent_final") &&
                            "rtl-text",
                        )}
                      >
                        {console.log("🎯 RENDERING AGENT MESSAGE:", {
                          messageId: msg.id,
                          agentIdName: msg.agentIdName,
                          messageLength: msg.message?.length,
                          messagePreview: msg.message?.substring(0, 50),
                          workflowId: msg.workflowId,
                          requestId: msg.requestId
                        })}
                        <div className={styles.agentUI}>
                          <AgentResponse
                            content={msg.message}
                            agentId={msg.agentIdName}
                            inputRef={inputRef}
                            currentChat={currentChat}
                            messageId={msg.id}
                          />
                        </div>
                        {/* <MarkdownRenderer content={msg.message.toString()} /> */}
                      </div>
                    </div>
                  )}
                </div>
                {msg.sender === "agent" &&
                  msg.agentIdName &&
                  !feedbackExcludedAgents.includes(msg.agentIdName) && (
                    <div className={styles.chatActions}>
                      {visibleIcons.map((icon) => (
                        <div
                          key={icon.id}
                          ref={(el) => {
                            if (el && (icon.id === 0 || icon.id === 1)) {
                              actionRefsMap.current.set(
                                `${msg.id}-${icon.id}`,
                                el,
                              );
                            }
                          }}
                          onClick={() => {
                            const anchorEl = actionRefsMap.current.get(
                              `${msg.id}-${icon.id}`,
                            );
                            handleIconClick(
                              icon.id,
                              msg.id,
                              msg.message,
                              msg.agentIdName,
                              msg.chatId,
                              msg.userPreference,
                              anchorEl,
                            );
                          }}
                          style={{ display: "inline-block", cursor: "pointer" }}
                        >
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className={styles.svgActionIcon}
                            style={{ cursor: "pointer" }}
                            // title={icon.id === 3 ? (copiedMessageId === msg.id ? "Copied!" : "Copy") : ""}
                          >
                            <rect
                              width="23.1233"
                              height="23.1233"
                              rx={icon.rectRadius || 2.52}
                              fill="white"
                            />
                            <rect
                              x="0.25"
                              y="0.25"
                              width="22.6233"
                              height="22.6233"
                              rx={icon.rectRadius || 4}
                              className={clsx(
                                styles.svgActionBorder,
                                ((icon.id === 3 &&
                                  copiedMessageId === msg.id) ||
                                  (icon.id === 0 && msg.userPreference === 1) ||
                                  (icon.id === 1 &&
                                    msg.userPreference === 0)) &&
                                  styles.svgActionBorderActive,
                              )}
                              strokeWidth="0.5"
                            />
                            {icon.paths.map((d, idx) => (
                              <path
                                key={idx}
                                d={d}
                                className={clsx(
                                  styles.iconPath,
                                  ((icon.id === 3 &&
                                    copiedMessageId === msg.id) ||
                                    (icon.id === 0 &&
                                      msg.userPreference === 1) ||
                                    (icon.id === 1 &&
                                      msg.userPreference === 0)) &&
                                    styles.iconPathActive,
                                )}
                              />
                            ))}
                          </svg>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>
          );
        })}
        {/* Show stream progress tracker when streaming */}
        {(loading || isStreaming) && (
          <div className={clsx(styles.messageBubble, styles.agentBubble)}>
            <div className={styles.avatar}>
              <img
                src={agentAvatar}
                alt="Agent avatar"
                height={24}
                aria-label="Agent avatar"
                role="img"
              />
            </div>
            <StreamProgressTracker
              isImageGeneration={getIsImageGenerationFlag(currentChat.length)}
            />
          </div>
        )}
      </div>
      <ThoughtsDrawer
        title="Chain of thoughts"
        drawerOpen={drawerOpen}
        onCloseDrawer={() => setDrawerOpen(false)}
      >
        <AgentTimeline timeline={timelineData} />
      </ThoughtsDrawer>

      {/* Feedback Popup */}
      <Popover
        open={Boolean(feedbackPopupState.anchorEl)}
        anchorEl={feedbackPopupState.anchorEl}
        onClose={() => {
          console.log("Closing feedback popup");
          setFeedbackPopupState({ messageId: null, anchorEl: null });
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              borderRadius: 2,
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
              maxWidth: 320,
              minWidth: 280,
              mb: 1,
            },
          },
        }}
      >
        <Box sx={{ position: "relative", p: 2.5 }}>
          <IconButton
            size="small"
            onClick={() => {
              console.log("Close button clicked");
              setFeedbackPopupState({ messageId: null, anchorEl: null });
            }}
            sx={{
              position: "absolute",
              right: 4,
              top: 4,
              color: "grey.500",
              "&:hover": {
                backgroundColor: "grey.100",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              mt: 2,
              color: "text.primary",
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            Please provide detailed{" "}
            <a
              style={{
                cursor: "pointer",
                color: "#00d2aa",
                textDecoration: "underline",
              }}
              onClick={() => {
                console.log("Provide Feedback button clicked");
                window.open("/feedback", "_blank");
                setFeedbackPopupState({ messageId: null, anchorEl: null });
              }}
            >
              Feedback
            </a>
          </Typography>
        </Box>
      </Popover>
    </div>
  );
};

export default MessageBubble;
